import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';

import { DateRangePickerV2, Radio, Select } from '@utilities';

import styles from './_index.module.scss';

const DateRangeOptions = ({ activePrompt, activePromptAnswers, onAnswerChange, promptKey }) => {
  const [customDates, setCustomDates] = useState([null, null]);
  const [isCustomChecked, setIsCustomChecked] = useState(false);
  const [startDate, endDate] = customDates;

  const selectedValue = activePromptAnswers?.[promptKey];

  useEffect(() => {
    if (!selectedValue && activePrompt?.dateOptions?.length > 0) {
      const defaultOption = activePrompt.dateOptions
        .flatMap((dateOption) => dateOption.options)
        .find((option) => option.isDefault);

      if (defaultOption) {
        onAnswerChange({ [promptKey]: defaultOption.value });
      }
    }
  }, [activePrompt, onAnswerChange, promptKey, selectedValue]);

  useEffect(() => {
    if (!selectedValue) {
      setIsCustomChecked(false);
      setCustomDates([null, null]);
    }
  }, [selectedValue]);

  useEffect(() => {
    if (isCustomChecked && !(customDates.includes(null) || customDates.includes(undefined))) {
      onAnswerChange({ [promptKey]: customDates });
    }
  }, [customDates, isCustomChecked]);

  useEffect(() => {
    const hasDateOptionAnswer = some(
      activePrompt?.dateOptions?.reduce((a, b) => a.concat(b?.options), []),
      ['value', selectedValue]
    );
    const hasCustomDateAnswer =
      selectedValue &&
      Array.isArray(selectedValue) &&
      selectedValue?.length === 2 &&
      !hasDateOptionAnswer;

    if (
      selectedValue &&
      (hasCustomDateAnswer ||
        (activePrompt?.dateOptions?.length === 0 &&
          activePrompt?.hasCustomDateOption &&
          !selectedValue?.[0]?.id?.includes('date_group')))
    ) {
      setCustomDates(selectedValue);
      setIsCustomChecked(true);
    } else {
      setCustomDates([null, null]);
      setIsCustomChecked(false);
    }
  }, [activePrompt]);

  return (
    <div className={styles['date-range-options']}>
      <ul>
        {activePrompt?.dateOptions.map((dateOption, index) => {
          const { options, timeFrame } = dateOption;
          const isChecked =
            !isCustomChecked && options?.some((option) => isEqual(option.value, selectedValue));

          const selectedOption = options?.find((option) => isEqual(option.value, selectedValue));

          const handleRadioChange = () => {
            const defaultValue = options?.[0]?.value;
            if (Array.isArray(defaultValue) && defaultValue.length > 0) {
              onAnswerChange({ [promptKey]: defaultValue });
            }
            setIsCustomChecked(false);
          };

          const handleSelectChange = (option) => {
            onAnswerChange({ [promptKey]: option.value });
          };

          return (
            <li key={`date-option-${index}`}>
              <div className={isChecked ? styles['is-selected'] : ''}>
                <Radio
                  isChecked={isChecked}
                  isInverseColor
                  label={timeFrame}
                  onChange={handleRadioChange}
                  value={options?.[0]?.value}
                />
              </div>
              {isChecked && options?.length > 1 && (
                <div className={styles['date-range-options-select-container']}>
                  <Select
                    onChange={handleSelectChange}
                    options={options}
                    value={selectedOption || null}
                  />
                </div>
              )}
            </li>
          );
        })}
        {activePrompt?.hasCustomDateOption && (
          <li>
            <div className={isCustomChecked ? styles['is-selected'] : ''}>
              <Radio
                isChecked={isCustomChecked}
                isInverseColor
                label="Custom Time Period"
                name={activePrompt?.promptKey}
                onChange={() => setIsCustomChecked(true)}
              />
            </div>
          </li>
        )}
      </ul>
      {isCustomChecked && (
        <div className={styles['date-range-options-custom-date-range']}>
          <DateRangePickerV2
            endDate={endDate}
            maxDate={activePrompt?.maxDate}
            onDatesChange={setCustomDates}
            startDate={startDate}
          />
        </div>
      )}
    </div>
  );
};

DateRangeOptions.propTypes = {
  activePrompt: PropTypes.object,
  activePromptAnswers: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onAnswerChange: PropTypes.func.isRequired,
  promptKey: PropTypes.string.isRequired,
};

export default DateRangeOptions;
